import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from 'react-router/dom';
import { Toaster } from 'sonner';
import './styles.css';
import { AuthProvider } from '@/app/auth';
import { queryClient } from '@/app/query';
import { ThemeProvider, useTheme } from '@/app/theme';
import { ConfirmProvider } from '@/components/ui/confirm';
import { TooltipProvider } from '@/components/ui/overlay';
import { installApi } from '@/lib/api';
import { captureAttribution } from '@/lib/attribution';
import { initAnalytics } from '@/lib/analytics';
import { captureInstallPrompt } from '@/lib/install-app';
import { config } from '@/lib/config';
import { router } from './routes';

function ThemedToaster() {
  const { dark } = useTheme();
  return (
    <Toaster
      theme={dark ? 'dark' : 'light'}
      // Top right: at the bottom, toasts covered the main button in setup and dialogs.
      position="top-right"
      closeButton
      richColors
      toastOptions={{ duration: 4000 }}
    />
  );
}

function ScrollbarAppearance() {
  useEffect(() => {
    const timers = new Map<Element, ReturnType<typeof setTimeout>>();
    const scrolling = (event: Event) => {
      const target = event.target === document ? document.documentElement : event.target;
      if (!(target instanceof Element)) return;
      clearTimeout(timers.get(target));
      target.setAttribute('data-scrolling', '');
      timers.set(
        target,
        setTimeout(() => {
          target.removeAttribute('data-scrolling');
          timers.delete(target);
        }, 650),
      );
    };
    document.addEventListener('scroll', scrolling, { capture: true, passive: true });
    return () => {
      document.removeEventListener('scroll', scrolling, true);
      timers.forEach((timer, target) => {
        clearTimeout(timer);
        target.removeAttribute('data-scrolling');
      });
    };
  }, []);
  return null;
}

function FocusAppearance() {
  useEffect(() => {
    const root = document.documentElement;
    root.dataset.inputMethod = 'pointer';
    const pointer = () => {
      root.dataset.inputMethod = 'pointer';
    };
    const keyboard = (event: KeyboardEvent) => {
      if (['Tab', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter', ' '].includes(event.key))
        root.dataset.inputMethod = 'keyboard';
    };
    document.addEventListener('pointerdown', pointer, true);
    document.addEventListener('keydown', keyboard, true);
    return () => {
      document.removeEventListener('pointerdown', pointer, true);
      document.removeEventListener('keydown', keyboard, true);
      delete root.dataset.inputMethod;
    };
  }, []);
  return null;
}

async function start() {
  captureAttribution();
  initAnalytics();
  captureInstallPrompt();
  // The worker makes the dashboard installable; the dev server keeps working without it.
  if (import.meta.env.PROD && 'serviceWorker' in navigator)
    window.addEventListener('load', () => void navigator.serviceWorker.register('/sw.js').catch(() => {}));

  if (config.isDemo) {
    const { demoApi } = await import('./demo');
    installApi(demoApi);
  }

  // Vite may reload this module when route exports change; reuse the mounted root.
  const root = import.meta.hot?.data.root ?? createRoot(document.getElementById('root')!);
  if (import.meta.hot) import.meta.hot.data.root = root;
  root.render(
    <StrictMode>
      <FocusAppearance />
      <ScrollbarAppearance />
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          <TooltipProvider delayDuration={300}>
            <AuthProvider>
              <ConfirmProvider>
                <RouterProvider router={router} />
              </ConfirmProvider>
            </AuthProvider>
            <ThemedToaster />
          </TooltipProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </StrictMode>,
  );
}

start();
